import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { landlordApi, orderService } from '../../services/api';

interface Order {
  id: string;
  certificateType: string;
  propertyId: string;
  propertyAddress?: string;
  status: 'pending' | 'assigned' | 'in_progress' | 'completed' | 'cancelled';
  createdAt: string;
  updatedAt?: string;
  completedAt?: string;
  partnerId?: string;
  partnerName?: string;
  estimatedCompletionDate?: string;
  notes?: string;
  dateRange?: { startDate: string; endDate: string };
  accessDetails?: { contactName: string; contactPhone: string; accessInstructions: string; accessMethod: string };
}

interface TimelineEvent {
  timestamp: string;
  status: string;
  description: string;
  icon: string;
}

interface OrderTrackingProps {
  order?: Order;
  onUpdate?: (updatedOrder: Order) => void;
}

export default function OrderTracking({ order: propOrder, onUpdate }: OrderTrackingProps = {}) {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const [internalOrder, setInternalOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(!propOrder);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  const order = propOrder || internalOrder;

  useEffect(() => {
    if (!propOrder && orderId) {
      loadOrder();
      // Poll for updates every 30 seconds
      const interval = setInterval(loadOrder, 30000);
      return () => clearInterval(interval);
    }
  }, [orderId, propOrder]);

  const loadOrder = async () => {
    try {
      setError(null);
      const response = await landlordApi.get(`api/orders/${orderId}`);
      setInternalOrder(response.data.data || response.data);
    } catch (err: any) {
      console.error('[OrderTracking] Load failed:', err);
      if (err?.response?.status === 404) {
        setError('Order not found');
      } else if (!loading) {
        setError(err instanceof Error ? err.message : 'Failed to load order');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadOrder();
  };

  const getProgressPercentage = (status: string) => {
    switch (status) {
      case 'pending':
        return 25;
      case 'assigned':
        return 50;
      case 'in_progress':
        return 75;
      case 'completed':
        return 100;
      case 'cancelled':
        return 0;
      default:
        return 0;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'green';
      case 'in_progress':
        return 'blue';
      case 'assigned':
        return 'yellow';
      case 'pending':
        return 'gray';
      case 'cancelled':
        return 'red';
      default:
        return 'gray';
    }
  };

  const buildTimeline = (): TimelineEvent[] => {
    if (!order) return [];

    const timeline: TimelineEvent[] = [
      {
        timestamp: order.createdAt,
        status: 'pending',
        description: 'Order created',
        icon: '📋',
      },
    ];

    if (order.status !== 'pending') {
      timeline.push({
        timestamp: order.updatedAt || new Date().toISOString(),
        status: 'assigned',
        description: 'Assigned to ' + (order.partnerName || 'partner'),
        icon: '👤',
      });
    }

    if (order.status === 'in_progress' || order.status === 'completed') {
      timeline.push({
        timestamp: order.updatedAt || new Date().toISOString(),
        status: 'in_progress',
        description: 'Work in progress',
        icon: '⟳',
      });
    }

    if (order.status === 'completed') {
      timeline.push({
        timestamp: order.completedAt || new Date().toISOString(),
        status: 'completed',
        description: 'Certificate completed',
        icon: '✓',
      });
    }

    if (order.status === 'cancelled') {
      timeline.push({
        timestamp: order.updatedAt || new Date().toISOString(),
        status: 'cancelled',
        description: 'Order cancelled',
        icon: '✕',
      });
    }

    return timeline;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatDateShort = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      month: 'short',
      day: 'numeric',
    });
  };

  const getDaysRemaining = (estimatedDate?: string) => {
    if (!estimatedDate) return null;
    const days = Math.ceil(
      (new Date(estimatedDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)
    );
    return days > 0 ? days : 0;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-16">
        <div className="animate-pulse flex flex-col items-center">
          <div className="h-12 w-12 bg-emerald-200 rounded-full mb-4"></div>
          <p className="text-gray-600">Loading order details...</p>
        </div>
      </div>
    );
  }

  if (!order || error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <p className="text-red-800 font-medium mb-4">{error || 'Order not found'}</p>
        <button
          onClick={() => navigate('/certificate-orders')}
          className="inline-flex items-center px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 font-medium"
        >
          Back to Orders
        </button>
      </div>
    );
  }

  const timeline = buildTimeline();
  const statusColor = getStatusColor(order.status);
  const daysRemaining = getDaysRemaining(order.estimatedCompletionDate);

  return (
    <div className="max-w-3xl mx-auto">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm p-4 md:p-6 mb-6">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
              Order #{order.id}
            </h1>
            <p className="text-gray-600 text-sm mt-1">
              Created on {formatDate(order.createdAt)}
            </p>
          </div>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className={`self-start md:self-auto px-4 py-2 rounded-lg font-medium text-sm transition ${
              refreshing
                ? 'bg-gray-200 text-gray-600 cursor-not-allowed'
                : 'bg-emerald-600 text-white hover:bg-emerald-700'
            }`}
          >
            {refreshing ? '⟳ Refreshing...' : '🔄 Refresh'}
          </button>
        </div>

        {/* Certificate Type & Property */}
        <div className="space-y-3 pb-6 border-b border-gray-200">
          <div>
            <p className="text-xs text-gray-600 font-medium uppercase mb-1">Certificate Type</p>
            <p className="text-lg font-semibold text-gray-900">
              {order.certificateType
                .replace(/_/g, ' ')
                .split(' ')
                .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
                .join(' ')}
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-600 font-medium uppercase mb-1">Property</p>
            <p className="text-gray-900">
              {order.propertyAddress || `Property ${order.propertyId}`}
            </p>
          </div>
        </div>

        {/* Status Section */}
        <div className="mt-6">
          <p className="text-xs text-gray-600 font-medium uppercase mb-3">Order Status</p>
          <div
            className={`inline-block px-4 py-2 rounded-full font-medium text-white bg-${statusColor}-600`}
          >
            {order.status.replace(/_/g, ' ').toUpperCase()}
          </div>

          {/* Progress Bar */}
          <div className="mt-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-gray-700">Progress</span>
              <span className="text-xs font-medium text-gray-700">
                {getProgressPercentage(order.status)}%
              </span>
            </div>
            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className={`h-full bg-${statusColor}-500 transition-all duration-500`}
                style={{ width: `${getProgressPercentage(order.status)}%` }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Estimated Completion */}
      {order.estimatedCompletionDate && order.status !== 'completed' && order.status !== 'cancelled' && (
        <div className={`bg-${statusColor}-50 border border-${statusColor}-200 rounded-lg p-4 md:p-6 mb-6`}>
          <p className={`text-sm font-medium text-${statusColor}-900 mb-1`}>
            Estimated Completion
          </p>
          <p className={`text-2xl font-bold text-${statusColor}-700`}>
            {formatDateShort(order.estimatedCompletionDate)}
          </p>
          {daysRemaining !== null && (
            <p className={`text-xs text-${statusColor}-700 mt-1`}>
              {daysRemaining === 0
                ? 'Due today'
                : daysRemaining === 1
                  ? 'Due tomorrow'
                  : `${daysRemaining} days remaining`}
            </p>
          )}
        </div>
      )}

      {/* Partner Assignment */}
      {order.partnerName && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 md:p-6 mb-6">
          <p className="text-xs text-blue-700 font-medium uppercase mb-1">Assigned Partner</p>
          <p className="text-lg font-semibold text-blue-900">{order.partnerName}</p>
          {order.status !== 'pending' && (
            <p className="text-sm text-blue-700 mt-2">
              This order has been assigned to a qualified partner for processing.
            </p>
          )}
        </div>
      )}

      {/* Access Details */}
      {order.accessDetails && (
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 md:p-6 mb-6">
          <h3 className="text-sm font-semibold text-purple-900 mb-4">👤 Engineer Access Details</h3>
          <div className="space-y-3 text-sm">
            {order.accessDetails.contactName && (
              <div>
                <p className="text-purple-700 font-medium">Contact Name</p>
                <p className="text-purple-900">{order.accessDetails.contactName}</p>
              </div>
            )}
            {order.accessDetails.contactPhone && (
              <div>
                <p className="text-purple-700 font-medium">Contact Phone</p>
                <p className="text-purple-900">{order.accessDetails.contactPhone}</p>
              </div>
            )}
            {order.accessDetails.accessMethod && (
              <div>
                <p className="text-purple-700 font-medium">Access Method</p>
                <p className="text-purple-900 capitalize">
                  {order.accessDetails.accessMethod.replace(/_/g, ' ')}
                </p>
              </div>
            )}
            {order.accessDetails.accessInstructions && (
              <div>
                <p className="text-purple-700 font-medium">Special Instructions</p>
                <p className="text-purple-900 whitespace-pre-wrap">
                  {order.accessDetails.accessInstructions}
                </p>
              </div>
            )}
          </div>
          <p className="text-xs text-purple-700 mt-4 pt-4 border-t border-purple-200">
            📅 The assigned partner will coordinate with you using these details to schedule the visit.
            Check your email and SMS for calendar availability updates.
          </p>
        </div>
      )}

      {/* Scheduling Info */}
      {order.dateRange && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 md:p-6 mb-6">
          <h3 className="text-sm font-semibold text-green-900 mb-4">📅 Requested Date Range</h3>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-green-700 font-medium">From</p>
              <p className="text-green-900">
                {new Date(order.dateRange.startDate).toLocaleDateString('en-GB', {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric',
                })}
              </p>
            </div>
            <div>
              <p className="text-green-700 font-medium">To</p>
              <p className="text-green-900">
                {new Date(order.dateRange.endDate).toLocaleDateString('en-GB', {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric',
                })}
              </p>
            </div>
          </div>
          <p className="text-xs text-green-700 mt-4 pt-4 border-t border-green-200">
            ⚠️ Dates are <strong>unconfirmed</strong> until the partner manager schedules the visit.
            The actual date may differ based on partner availability in your area.
          </p>
        </div>
      )}

      {/* Timeline */}
      <div className="bg-white rounded-lg shadow-sm p-4 md:p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-6">Order Timeline</h2>
        <div className="space-y-4">
          {timeline.map((event, index) => (
            <div key={index} className="flex gap-4">
              {/* Timeline dot */}
              <div className="flex flex-col items-center">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center text-lg bg-${statusColor}-100`}
                >
                  {event.icon}
                </div>
                {index < timeline.length - 1 && (
                  <div className="w-0.5 h-8 bg-gray-200 my-1" />
                )}
              </div>

              {/* Content */}
              <div className="pt-1 flex-1">
                <p className="font-medium text-gray-900">{event.description}</p>
                <p className="text-sm text-gray-600 mt-1">
                  {formatDate(event.timestamp)}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Notes Section */}
      {order.notes && (
        <div className="bg-white rounded-lg shadow-sm p-4 md:p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-3">Notes</h2>
          <p className="text-gray-700 text-sm whitespace-pre-wrap">{order.notes}</p>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-3">
        <button
          onClick={() => navigate('/certificate-orders')}
          className="flex-1 px-4 py-3 rounded-lg border border-gray-300 text-gray-900 hover:bg-gray-50 font-medium transition"
        >
          Back to Orders
        </button>
        {order.status !== 'cancelled' && order.status !== 'completed' && (
          <button
            onClick={() => {
              // eslint-disable-next-line no-restricted-globals
              if (confirm('Are you sure you want to cancel this order?')) {
                // Use orderService to cancel order
                orderService.cancelOrder(order.id)
                  .then((response: any) => {
                    // Verify the response indicates success
                    if (response.data?.success || response.data?.data?.status === 'cancelled') {
                      // eslint-disable-next-line no-restricted-globals
                      alert('Order cancelled successfully');
                      // Navigate back to orders list
                      navigate('/certificate-orders');
                    } else {
                      // eslint-disable-next-line no-restricted-globals
                      alert('Failed to cancel order: ' + (response.data?.error?.message || 'Unknown error'));
                    }
                  })
                  .catch((err: any) => {
                    console.error('[OrderTracking] Cancel error:', err);
                    // eslint-disable-next-line no-restricted-globals
                    alert('Failed to cancel order: ' + (err.response?.data?.error?.message || err.message));
                  });
              }
            }}
            className="flex-1 px-4 py-3 rounded-lg border border-red-300 text-red-700 hover:bg-red-50 font-medium transition"
          >
            Cancel Order
          </button>
        )}
      </div>
    </div>
  );
}
